import { SupportedChain } from '../../constants';
import { getChainConfig, getNetworkIdByChain } from '../../config/chains';
import {
  ChainAdapter,
  TransactionResult,
  WalletInfo,
  TicketInfo,
  GroupInfo,
  JackpotInfo,
  WhitelistInfo,
  GroupIdentifier,
} from '../interfaces/ChainAdapter';
import { Web3Provider } from '@ethersproject/providers';
import { parseEther, formatEther } from '@ethersproject/units';
import { isAddress } from '@ethersproject/address';
import { getLixiBagContract } from '../../web3/utils/getLixiBagContract';
import { signMessage } from '../../utils/signMessage';
import { createBNBToken } from '../../utils/createBNBToken';
import { Contract } from '@ethersproject/contracts';
import ERC20Abi from '../../abi/ERC20.json';
import LixiBagAbi from '../../abi/LixiBag.json';
// removed recursive viaChainManager helpers; use direct web3 calls instead

// Helper for development-only logging
const devLog = (...args: any[]) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[BNBAdapter]', ...args);
  }
};

export class BNBAdapter implements ChainAdapter {
  readonly chain: SupportedChain = 'bnb';
  readonly config = getChainConfig('bnb');
  private listeners: Map<string, ((...args: any[]) => void)[]> = new Map();
  private isConnecting = false;
  private lastEthereumProvider: any = null;

  constructor() {
    // Keep initialization minimal to avoid interfering with other wallets (e.g., Phantom)
  }

  private ensureGroupId(group: GroupIdentifier | undefined): string {
    if (!group?.groupId) {
      throw new Error('groupId is required for BNB operations');
    }
    return group.groupId;
  }

  /**
   * Validate BNB/Ethereum address (0x format)
   * @param address - Address string to validate
   * @returns true if address is valid Ethereum/BNB address, false otherwise
   */
  validateAddress(address: string): boolean {
    if (!address || address.trim().length === 0) {
      return false;
    }

    const trimmedAddr = address.trim();

    // BNB/Ethereum addresses must start with 0x
    if (!trimmedAddr.startsWith('0x')) {
      return false;
    }

    // Use ethers isAddress to validate
    return isAddress(trimmedAddr);
  }

  private getConsistentProvider(): any {
    // Always try to find fresh MetaMask provider first
    // This prevents issues with stale cached providers
    const freshProvider = this.findMetaMaskProvider();

    if (freshProvider) {
      // Update cached provider if we found a fresh one
      this.lastEthereumProvider = freshProvider;
      return freshProvider;
    }

    // Fallback to cached provider only if fresh detection fails
    if (this.lastEthereumProvider) {
      devLog('BNBAdapter: Using cached provider as fallback');
      return this.lastEthereumProvider;
    }

    return null;
  }

  private findMetaMaskProvider(): any | null {
    if (typeof window === 'undefined') return null;

    const ethereum = (window as any).ethereum;
    if (!ethereum) return null;

    // Priority 1: Direct MetaMask provider (most reliable)
    if (ethereum.isMetaMask && !ethereum.isPhantom) {
      devLog('BNBAdapter: Found direct MetaMask provider');
      return ethereum;
    }

    // Priority 2: Check providers array (common when multiple wallets installed)
    if (Array.isArray(ethereum.providers)) {
      devLog(
        'BNBAdapter: Checking providers array, found',
        ethereum.providers.length,
        'providers',
      );

      // Look for MetaMask in providers array
      const metamaskProvider = ethereum.providers.find((p: any) => {
        return p && p.isMetaMask && !p.isPhantom;
      });

      if (metamaskProvider) {
        devLog('BNBAdapter: Found MetaMask in providers array');
        return metamaskProvider;
      }

      // Log all providers for debugging
      ethereum.providers.forEach((provider: any, index: number) => {
        devLog(`BNBAdapter: Provider ${index}:`, {
          isMetaMask: Boolean(provider?.isMetaMask),
          isPhantom: Boolean(provider?.isPhantom),
          selectedAddress: provider?.selectedAddress,
        });
      });
    }

    // Priority 3: Check detected array (less common)
    if (Array.isArray(ethereum.detected)) {
      devLog(
        'BNBAdapter: Checking detected array, found',
        ethereum.detected.length,
        'detected providers',
      );

      const metamaskProvider = ethereum.detected.find((p: any) => {
        return p && p.isMetaMask && !p.isPhantom;
      });

      if (metamaskProvider) {
        devLog('BNBAdapter: Found MetaMask in detected array');
        return metamaskProvider;
      }
    }

    // Priority 4: Check if window.ethereum is an array (rare case)
    if (Array.isArray(ethereum)) {
      devLog('BNBAdapter: window.ethereum is an array, checking for MetaMask');

      const metamaskProvider = ethereum.find((p: any) => {
        return p && p.isMetaMask && !p.isPhantom;
      });

      if (metamaskProvider) {
        devLog('BNBAdapter: Found MetaMask in window.ethereum array');
        return metamaskProvider;
      }
    }

    // Priority 5: Check for Phantom interference
    if (ethereum.isPhantom) {
      devLog(
        'BNBAdapter: Phantom wallet detected, this may interfere with MetaMask',
      );
    }

    devLog('BNBAdapter: No MetaMask provider found');
    return null;
  }

  // Helper method to add BNB network to MetaMask
  private async addBNBNetwork(ethereumProvider: any): Promise<void> {
    try {
      const { NETWORKS } = await import('../../web3/networks');
      const chainId = getNetworkIdByChain(this.chain);
      if (!chainId) {
        return;
      }
      const network = NETWORKS[chainId];

      if (!network) {
        return;
      }

      const toHex = (num: number) => '0x' + num.toString(16);

      const networkParams = {
        chainId: toHex(network.chainId),
        chainName: network.name,
        nativeCurrency: {
          name: network.nativeCurrency.name,
          symbol: network.nativeCurrency.symbol,
          decimals: network.nativeCurrency.decimals,
        },
        rpcUrls: network.rpc,
        blockExplorerUrls:
          network.explorers && network.explorers.length > 0
            ? [network.explorers[0].url]
            : [network.infoURL],
      };

      await ethereumProvider.request({
        method: 'wallet_addEthereumChain',
        params: [networkParams],
      });
    } catch (error: any) {
      // If network already exists, this is expected and not an error
      if (error?.code !== 4902) {
        console.warn('Failed to add BNB network to MetaMask:', error);
      }
    }
  }

  // Helper method to switch to BNB network
  private async switchToBNBNetwork(ethereumProvider: any): Promise<void> {
    try {
      const chainId = getNetworkIdByChain(this.chain);
      if (!chainId) {
        throw new Error('Could not get chain ID for BNB');
      }
      const toHex = (num: number) => '0x' + num.toString(16);

      await ethereumProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: toHex(chainId) }],
      });
    } catch (error: any) {
      // If network doesn't exist, try to add it
      if (error?.code === 4902) {
        await this.addBNBNetwork(ethereumProvider);
        // Try switching again after adding
        await this.switchToBNBNetwork(ethereumProvider);
      } else {
        console.warn('Failed to switch to BNB network:', error);
        throw error;
      }
    }
  }

  // Wallet management
  async connectWallet(): Promise<WalletInfo> {
    try {
      if (this.isConnecting) {
        throw new Error('Đang kết nối MetaMask, vui lòng đợi...');
      }
      this.isConnecting = true;

      // Import debug utility for better error reporting
      const { debugMetaMaskConnection } = await import(
        '../../utils/metamaskDebug'
      );

      // Try to find MetaMask provider with retry mechanism without touching Phantom
      let ethereumProvider = this.findMetaMaskProvider();

      // Retry a few times briefly in case providers array populates late
      for (let i = 0; i < 2 && !ethereumProvider; i++) {
        await new Promise(resolve => setTimeout(resolve, 200));
        ethereumProvider = this.findMetaMaskProvider();
      }

      if (!ethereumProvider) {
        const hasEthereum =
          typeof window !== 'undefined' && (window as any).ethereum;

        // Get detailed debug info for better error reporting
        const debugInfo = debugMetaMaskConnection();
        devLog('BNBAdapter: MetaMask debug info:', debugInfo);

        if (hasEthereum) {
          // Check if Phantom is interfering
          if (debugInfo.providerInfo.isPhantom) {
            throw new Error(
              'Phantom wallet đang can thiệp vào kết nối MetaMask. Vui lòng tắt Phantom wallet tạm thời hoặc sử dụng trình duyệt riêng.',
            );
          }

          // Check if there are multiple providers
          if (debugInfo.providers.length > 1) {
            const metamaskProvider = debugInfo.providers.find(
              p => p.isMetaMask && !p.isPhantom,
            );
            if (!metamaskProvider) {
              throw new Error(
                'MetaMask không được tìm thấy trong các provider có sẵn. Có thể có xung đột với wallet khác.',
              );
            }
          }

          throw new Error(
            'MetaMask không được tìm thấy trong các provider có sẵn. Vui lòng đảm bảo MetaMask đã được cài đặt và kích hoạt.',
          );
        } else {
          throw new Error(
            'MetaMask không được phát hiện. Vui lòng cài đặt MetaMask để sử dụng BNB chain.',
          );
        }
      }

      devLog('BNBAdapter: Using MetaMask for BNB chain');

      // Ensure provider is actually MetaMask
      if (
        !(ethereumProvider as any).isMetaMask ||
        (ethereumProvider as any).isPhantom
      ) {
        throw new Error(
          'Provider không phải MetaMask. Có thể Phantom wallet đang can thiệp.',
        );
      }

      // Store the provider for consistent use in transactions
      this.lastEthereumProvider = ethereumProvider;

      // Try to switch to BNB network first (this will add it if it doesn't exist)
      try {
        await this.switchToBNBNetwork(ethereumProvider);
      } catch (error) {
        devLog(
          'BNBAdapter: Failed to switch/add BNB network, continuing with connection:',
          error,
        );
        // Continue with connection even if network switch fails
      }

      // Request account access directly from MetaMask with retry logic
      let accounts: string[] | undefined;
      let requestError: any;
      const maxRetries = 3;
      const retryDelay = 500;

      for (let i = 0; i < maxRetries; i++) {
        try {
          devLog(
            `BNBAdapter: Requesting accounts (attempt ${i + 1}/${maxRetries})`,
          );
          accounts = await (ethereumProvider as any).request({
            method: 'eth_requestAccounts',
          });
          devLog('BNBAdapter: Successfully got accounts:', accounts);
          break;
        } catch (err: any) {
          requestError = err;
          const msg = (err?.message || '').toLowerCase();
          console.warn(
            `BNBAdapter: Request failed (attempt ${i + 1}/${maxRetries}):`,
            msg,
          );

          // Handle specific error cases with retry
          if (
            msg.includes('resource not available') ||
            msg.includes('already processing') ||
            msg.includes('user rejected') ||
            msg.includes('unauthorized')
          ) {
            if (i < maxRetries - 1) {
              devLog(`BNBAdapter: Retrying in ${retryDelay}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              continue;
            }
          }

          // Don't retry for user rejection or other permanent errors
          if (msg.includes('user rejected') || msg.includes('unauthorized')) {
            throw new Error(
              'Người dùng đã từ chối kết nối. Vui lòng thử lại và chấp nhận kết nối trong MetaMask.',
            );
          }

          throw err;
        }
      }
      if (!accounts || accounts.length === 0) {
        if (requestError) {
          throw new Error(
            `Không thể lấy tài khoản từ MetaMask: ${
              requestError.message || 'Unknown error'
            }`,
          );
        }
        throw new Error('Không tìm thấy tài khoản. Vui lòng kết nối MetaMask.');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const address = await signer.getAddress();
      const balance = await web3Provider.getBalance(address);

      const walletInfo: WalletInfo = {
        address,
        balance: formatEther(balance),
        symbol: this.config.symbol,
        isConnected: true,
        chain: this.chain,
      };

      // Emit wallet change event
      this.emit('walletChanged', walletInfo);

      return walletInfo;
    } catch (error) {
      // Enhanced error reporting with debug info
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      devLog('BNBAdapter: Connection failed with error:', errorMessage);

      // Provide more specific error messages
      if (
        errorMessage.includes('User rejected') ||
        errorMessage.includes('user rejected')
      ) {
        throw new Error(
          'Kết nối bị từ chối. Vui lòng thử lại và chấp nhận kết nối trong MetaMask.',
        );
      }

      if (errorMessage.includes('Phantom')) {
        throw new Error(
          'Phantom wallet đang can thiệp. Vui lòng tắt Phantom wallet tạm thời.',
        );
      }

      throw new Error(`Không thể kết nối BNB wallet: ${errorMessage}`);
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnectWallet(): Promise<void> {
    try {
      // Clear wallet state and stored provider
      this.lastEthereumProvider = null;
      this.emit('walletChanged', null);
      devLog('BNBAdapter: Disconnected from BNB wallet');
    } catch (error) {
      throw new Error(
        `Failed to disconnect BNB wallet: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  // Method to clear cached provider (useful for troubleshooting)
  clearCachedProvider(): void {
    devLog('BNBAdapter: Clearing cached provider');
    this.lastEthereumProvider = null;
  }

  // Method to force refresh provider (useful when connection issues persist)
  async refreshProvider(): Promise<boolean> {
    try {
      devLog('BNBAdapter: Force refreshing provider');
      this.lastEthereumProvider = null;

      const freshProvider = this.findMetaMaskProvider();
      if (freshProvider) {
        this.lastEthereumProvider = freshProvider;
        devLog('BNBAdapter: Provider refreshed successfully');
        return true;
      }

      devLog('BNBAdapter: No fresh provider found after refresh');
      return false;
    } catch (error) {
      devLog('BNBAdapter: Error refreshing provider:', error);
      return false;
    }
  }

  async getWalletInfo(): Promise<WalletInfo | null> {
    try {
      const ethereumProvider = this.findMetaMaskProvider();

      if (!ethereumProvider || !(ethereumProvider as any).selectedAddress) {
        // Clear cached provider if it's no longer valid
        if (
          this.lastEthereumProvider &&
          !this.lastEthereumProvider.selectedAddress
        ) {
          devLog('BNBAdapter: Cached provider no longer valid, clearing cache');
          this.lastEthereumProvider = null;
        }
        return null;
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const address = await signer.getAddress();
      const balance = await web3Provider.getBalance(address);

      return {
        address,
        balance: formatEther(balance),
        symbol: this.config.symbol,
        isConnected: true,
        chain: this.chain,
      };
    } catch (error) {
      // Clear cached provider if there's an error accessing it
      if (this.lastEthereumProvider) {
        devLog('BNBAdapter: Error accessing cached provider, clearing cache');
        this.lastEthereumProvider = null;
      }
      return null;
    }
  }

  async signMessage(): Promise<string> {
    try {
      devLog('BNBAdapter: Starting signMessage process');

      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('No MetaMask provider found');
      }

      devLog('BNBAdapter: Provider found, creating Web3Provider');
      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();

      devLog(
        'BNBAdapter: Signer created, creating BNB token with blockchain header',
      );
      // Use the new createBNBToken utility with same format as Solana
      const signedToken = await createBNBToken(signer, '30d');
      devLog(
        'BNBAdapter: BNB token created successfully, token length:',
        signedToken.length,
      );

      return signedToken;
    } catch (error) {
      devLog('BNBAdapter: Error creating BNB token:', error);
      console.error('BNBAdapter: Full error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error,
      });
      throw new Error(
        `Failed to create BNB token: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      );
    }
  }

  async isWalletConnected(): Promise<boolean> {
    try {
      const ethereumProvider = this.findMetaMaskProvider();

      if (ethereumProvider && (ethereumProvider as any).selectedAddress) {
        devLog('BNBAdapter: MetaMask connected to BNB');
        return true;
      }

      // Clear cached provider if it's no longer connected
      if (
        this.lastEthereumProvider &&
        !this.lastEthereumProvider.selectedAddress
      ) {
        devLog(
          'BNBAdapter: Cached provider no longer connected, clearing cache',
        );
        this.lastEthereumProvider = null;
      }

      return false;
    } catch (error) {
      // Clear cached provider if there's an error
      if (this.lastEthereumProvider) {
        devLog(
          'BNBAdapter: Error checking cached provider connection, clearing cache',
        );
        this.lastEthereumProvider = null;
      }
      return false;
    }
  }

  // Core transactions
  async buyTickets(params: {
    group: GroupIdentifier;
    quantity: number;
    price: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      // Convert price to wei
      const value = parseEther(params.price);

      // For now, we'll use a simplified version. In a real implementation,
      // you'd need to handle extra tokens, receivers, etc.
      const tx = await contract.mintLixi(
        params.quantity,
        '0x0000000000000000000000000000000000000000', // No extra token
        '0', // No extra token amount
        [], // No receivers
        { value },
      );

      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimLixi(params: {
    group: GroupIdentifier;
    ticketId?: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const groupId = parseInt(this.ensureGroupId(params.group));
      const tx = await contract.claimLixi(groupId);
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimLixiByOwner(params: {
    group: GroupIdentifier;
    receiver: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }
      if (!this.validateAddress(params.receiver)) {
        throw new Error('Invalid receiver address');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const groupId = parseInt(this.ensureGroupId(params.group));
      const tx = await contract.claimLixiByOwner(groupId, params.receiver);
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimGlobalJackpot(): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.claimGlobalJackpot();
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async claimLocalJackpot?(params: {
    group: GroupIdentifier;
    groupId: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      // In BNB chain, groupId is used as ticketId
      const ticketId = parseInt(params.groupId);
      const tx = await contract.claimLocalJackpot(ticketId);
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Whitelist management
  async addWhitelister(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.addWhitelister(
        parseInt(this.ensureGroupId(params.group)),
        params.user,
      );
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async removeWhitelister(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.removeWhitelister(
        parseInt(this.ensureGroupId(params.group)),
        params.user,
      );
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async addManyWhitelisters(params: {
    group: GroupIdentifier;
    users: string[];
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.addManyWhitelisters(
        parseInt(this.ensureGroupId(params.group)),
        params.users,
      );
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // Configuration methods
  async setTicketPrice(params: {
    group: GroupIdentifier;
    price: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.setTicketPrice(
        parseInt(this.ensureGroupId(params.group)),
        parseEther(params.price),
      );
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setClosingTime(params: {
    group: GroupIdentifier;
    closingTime: number;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.setClosingTime(params.closingTime);
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setAdminStatus(params: {
    account: string;
    isAdmin: boolean;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.setAdminStatus(params.account, params.isAdmin);
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setJackpotConfig(params: {
    config: unknown;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      // This would need to be implemented based on the actual contract method
      const tx = await contract.setJackpotConfig(params.config);
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async setWinningHash(params: {
    group: GroupIdentifier;
    hash: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }

      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const contract = getLixiBagContract(
        this.config.contractAddress!,
        signer,
        true,
      );

      const tx = await contract.setWinningHash(
        parseInt(this.ensureGroupId(params.group)),
        params.hash,
      );
      const receipt = await tx.wait();

      return {
        success: true,
        hash: tx.hash,
        blockNumber: receipt.blockNumber,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // View methods
  async getTicketInfo(ticketId: string): Promise<TicketInfo | null> {
    try {
      const contract = getLixiBagContract(this.config.contractAddress!);
      const ticketInfo = await contract.ticketInfo(parseInt(ticketId));

      return {
        id: ticketId,
        owner: ticketInfo[1], // purchaser
        groupId: ticketInfo[0].toString(),
        isClaimed: false, // This would need to be determined from other contract calls
        price: '0', // This would need to be fetched from group info
        currency: this.config.symbol,
      };
    } catch (error) {
      console.error('Failed to get ticket info:', error);
      return null;
    }
  }

  async getGroupInfo(group: GroupIdentifier): Promise<GroupInfo | null> {
    try {
      const groupId = this.ensureGroupId(group);
      const contract = getLixiBagContract(this.config.contractAddress!);
      const groupInfo = await contract.groupInfo(parseInt(groupId));

      return {
        id: groupId,
        owner: groupInfo.groupOwner,
        ticketPrice: '0', // This would need to be fetched separately
        currency: this.config.symbol,
        closingTime: 0, // This would need to be fetched from contract
        isClosed: false, // This would need to be determined
        totalTickets: groupInfo.groupSize.toNumber(),
        claimedTickets: groupInfo.groupClaimed.toNumber(),
        jackpotAmount: groupInfo.groupLocalJackpotPrize.toString(),
        // BNB specific fields
        groupSize: groupInfo.groupSize,
        groupClaimed: groupInfo.groupClaimed,
        groupHash: groupInfo.groupHash,
        groupOwner: groupInfo.groupOwner,
        groupLocalJackpotToken: groupInfo.groupLocalJackpotToken,
        groupLocalJackpotTokenAmount: groupInfo.groupLocalJackpotTokenAmount,
        groupLocalJackpotPrize: groupInfo.groupLocalJackpotPrize,
        groupStartTicketId: groupInfo.groupStartTicketId,
        groupLocalJackpotWinner: groupInfo.groupLocalJackpotWinner,
      };
    } catch (error) {
      console.error('Failed to get group info:', error);
      return null;
    }
  }

  async getGlobalJackpotPrice(): Promise<JackpotInfo | null> {
    try {
      const contract = getLixiBagContract(this.config.contractAddress!);
      const globalJackpotPrice = await contract.globalJackpotPrice();

      return {
        globalJackpotPrice: globalJackpotPrice.toString(),
        currency: this.config.symbol,
        totalAmount: globalJackpotPrice.toString(),
      };
    } catch (error) {
      console.error('Failed to get global jackpot price:', error);
      return null;
    }
  }

  async isWhitelisted(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<WhitelistInfo | null> {
    try {
      const contract = getLixiBagContract(this.config.contractAddress!);
      const isWhitelisted = await contract.isWhitelister(
        parseInt(this.ensureGroupId(params.group)),
        params.user,
      );

      return {
        isWhitelisted,
        groupId: this.ensureGroupId(params.group),
        user: params.user,
      };
    } catch (error) {
      console.error('Failed to check whitelist status:', error);
      return null;
    }
  }

  // Event listeners
  onWalletChange(callback: (wallet: WalletInfo | null) => void): () => void {
    if (!this.listeners.has('walletChanged')) {
      this.listeners.set('walletChanged', []);
    }
    this.listeners.get('walletChanged')!.push(callback);

    return () => {
      const listeners = this.listeners.get('walletChanged');
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  onChainChange(callback: (chain: SupportedChain) => void): () => void {
    if (!this.listeners.has('chainChanged')) {
      this.listeners.set('chainChanged', []);
    }
    this.listeners.get('chainChanged')!.push(callback);

    return () => {
      const listeners = this.listeners.get('chainChanged');
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  private emit(event: string, ...args: any[]): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Unified approval helpers (EVM)
  async checkTokenApproval(params: {
    tokenAddress: string;
    spender: string;
    owner?: string;
    requiredAmountWei?: string;
  }): Promise<{ approved: boolean; allowanceWei: string }> {
    const ethereumProvider = this.getConsistentProvider();
    if (!ethereumProvider) {
      throw new Error('Wallet not connected');
    }
    const web3Provider = new Web3Provider(ethereumProvider as any);
    const signer = web3Provider.getSigner();
    const owner = params.owner || (await signer.getAddress());
    const erc20 = new Contract(params.tokenAddress, ERC20Abi, web3Provider);
    const allowance = await erc20.allowance(owner, params.spender);
    const allowanceWei = allowance.toString();
    const approved = params.requiredAmountWei
      ? allowance.gte(params.requiredAmountWei)
      : allowance.gt(0);
    return { approved, allowanceWei };
  }

  async approveToken(params: {
    tokenAddress: string;
    spender: string;
    amountWei?: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }
      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();
      const erc20 = new Contract(params.tokenAddress, ERC20Abi, signer);
      const amount =
        params.amountWei ||
        '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';
      const tx = await erc20.approve(params.spender, amount);
      const receipt = await tx.wait();
      return { success: true, hash: tx.hash, blockNumber: receipt.blockNumber };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  onTokenApproval?(
    tokenAddress: string,
    spender: string,
    callback: (data: {
      owner: string;
      spender: string;
      amountWei: string;
    }) => void,
  ): () => void {
    const ethereumProvider = this.getConsistentProvider();
    if (!ethereumProvider) {
      return () => void 0;
    }
    const web3Provider = new Web3Provider(ethereumProvider as any);
    const erc20 = new Contract(tokenAddress, ERC20Abi, web3Provider);
    const handler = (owner: string, _spender: string, amount: any) => {
      if (_spender?.toLowerCase() !== spender.toLowerCase()) return;
      callback({ owner, spender: _spender, amountWei: amount.toString() });
    };
    erc20.on('Approval', handler);
    return () => {
      try {
        erc20.off('Approval', handler);
      } catch (_) {
        // no-op
      }
    };
  }

  // Generic event subscription (EVM)
  subscribeToEvent?(
    eventName: string,
    callback: (data: any) => void,
    options?: { address?: string; filterArgs?: any[] },
  ): () => void {
    const ethereumProvider = this.getConsistentProvider();
    if (!ethereumProvider) {
      return () => void 0;
    }

    const web3Provider = new Web3Provider(ethereumProvider as any);

    // If no contract address provided, we can't subscribe to events
    if (!options?.address) {
      console.warn(
        'subscribeToEvent requires contract address in options.address',
      );
      return () => void 0;
    }

    // For now, we'll use LixiBag ABI for known events
    // In a real implementation, you'd have a contract registry or pass ABI
    try {
      const contractAddress = options.address;

      // Use LixiBag ABI for known events (MintLixi, SetWhitelister, etc.)
      // You can expand this to support other ABIs in the future
      const contract = new Contract(contractAddress, LixiBagAbi, web3Provider);

      // Set up event listener
      contract.on(eventName, (...args: any[]) => {
        // Convert event args to a more usable format
        const eventData = {
          eventName,
          chain: this.chain,
          timestamp: Date.now(),
          data: args.reduce((acc, arg, idx) => {
            // Handle BigNumber conversion
            if (arg?._hex) {
              acc[`arg${idx}`] = arg.toString();
            } else if (typeof arg === 'object' && arg !== null) {
              acc[`arg${idx}`] = arg.toString();
            } else {
              acc[`arg${idx}`] = arg;
            }
            return acc;
          }, {} as Record<string, any>),
        };

        callback(eventData);
      });

      return () => {
        try {
          contract.removeAllListeners(eventName);
        } catch (_) {
          // no-op
        }
      };
    } catch (error) {
      console.error(`Failed to subscribe to ${eventName}:`, error);
      return () => void 0;
    }
  }

  async getTokenDecimals(tokenAddress: string): Promise<number> {
    const ethereumProvider = this.getConsistentProvider();
    if (!ethereumProvider) {
      throw new Error('Wallet not connected');
    }
    const web3Provider = new Web3Provider(ethereumProvider as any);
    const erc20 = new Contract(tokenAddress, ERC20Abi, web3Provider);
    const decimals: number = await erc20.decimals();
    return decimals;
  }

  async sendRewardToMany(
    receivers: string[],
    _value: number,
  ): Promise<TransactionResult> {
    try {
      void _value; // value is currently determined inside contract helper
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }
      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();

      // Reuse existing write helper
      const { sendGasToMany } = await import(
        '../../web3/writeContract/sendGasToMany'
      );
      const tx = await sendGasToMany(
        receivers,
        signer,
        this.config.contractAddress!,
      );
      const receipt = await tx.wait();
      return { success: true, hash: tx.hash, blockNumber: receipt.blockNumber };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async createCampaign(params: {
    numMint: number;
    extraToken: string;
    extraTokenAmount: string;
    receivers: string[];
    value: number;
    lixiBagAddress: string;
  }): Promise<TransactionResult> {
    try {
      const ethereumProvider = this.getConsistentProvider();
      if (!ethereumProvider) {
        throw new Error('Wallet not connected');
      }
      const web3Provider = new Web3Provider(ethereumProvider as any);
      const signer = web3Provider.getSigner();

      const { createCampaignOnSC } = await import(
        '../../utils/contractInteraction'
      );
      const tx = await createCampaignOnSC(
        params.numMint,
        params.extraToken,
        params.extraTokenAmount,
        params.receivers,
        signer,
        params.value,
        params.lixiBagAddress,
      );
      const receipt = await tx.wait();
      return { success: true, hash: tx.hash, blockNumber: receipt.blockNumber };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async checkCampaignTimeStatus(lixiBagAddress: string): Promise<{
    currentTime: number;
    closingTime: number;
    timeRemaining: number;
    isExpired: boolean;
    closingTimeDate: Date;
    currentTimeDate: Date;
  }> {
    const ethereumProvider = this.getConsistentProvider();
    if (!ethereumProvider) {
      throw new Error('Wallet not connected');
    }
    const web3Provider = new Web3Provider(ethereumProvider as any);
    const { checkCampaignTimeStatus } = await import(
      '../../utils/contractInteraction'
    );
    return checkCampaignTimeStatus(lixiBagAddress, web3Provider);
  }
}
